package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.Period
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestamp
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.arenaproplay.admin.utils.Storage
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.mono
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

private const val DEFAULT_PERIOD_KEY = "arena.admin.default_period"

fun storedDefaultPeriod(): Period =
    Period.entries.find { it.code == Storage.get(DEFAULT_PERIOD_KEY) } ?: Period.MONTH

@Page("/settings")
@Composable
fun SettingsPage() {
    val strings = LocalAppStrings.current
    val container = LocalAppContainer.current
    val session = LocalAuthSession.current

    var defaultPeriod by remember { mutableStateOf(storedDefaultPeriod()) }
    var sidebarOpen by container.sidebarOpen

    // ── Analytics baseline ────────────────────────────────────────────────────
    val repo = container.adminRepository
    val scope = rememberCoroutineScope()
    var baseline by remember { mutableStateOf<String?>(null) }
    var showResetConfirm by remember { mutableStateOf(false) }
    var baselineBusy by remember { mutableStateOf(false) }
    var baselineError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(repo) {
        if (repo != null) {
            runCatching { baseline = repo.getAnalyticsBaseline() }
        }
    }

    AdminLayout(
        title = strings.navSettings,
        subtitle = "Настройки рабочего пространства",
    ) {
        // Админка русская by design — language switcher removed (was dead UI, Settings.kt)

        // ── Период по умолчанию ────────────────────────────────────────────────
        SectionCard(
            title = "Период по умолчанию",
            subtitle = "Диапазон дат, выбранный по умолчанию на дашбордах",
        ) {
            PeriodSelector(defaultPeriod) {
                defaultPeriod = it
                Storage.save(DEFAULT_PERIOD_KEY, it.code)
            }
        }

        VSpace(16)

        // ── Аналитика (базовая линия) ──────────────────────────────────────────
        SectionCard(
            title = "Аналитика",
            subtitle = if (baseline != null)
                "Отсчёт с ${formatTimestamp(baseline)} — старые данные скрыты, не удалены"
            else
                "Считается полная история",
            actions = {
                if (baseline != null) {
                    // Show current baseline state pill
                    Pill(text = "Отсчёт активен", tone = PillTone.Warning)
                }
            }
        ) {
            // Baseline status chip when set
            if (baseline != null) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .sBorderRadius(10)
                        .sPadding(12)
                        .styleModifier {
                            property("background", AppColors.Accent.WarningTint.toRgbaString(1f))
                            property("border", "1px solid ${AppColors.Warning.toRgbaString(0.20f)}")
                        }
                ) {
                    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        Column(Modifier.weight(1f)) {
                            SpanText(
                                "Отсчёт с ${formatTimestamp(baseline)}",
                                Modifier.bodyMMedium().color(AppColors.Ink)
                                    .styleModifier { property("font-weight", "700") },
                            )
                            Box(Modifier.height(2.px))
                            SpanText(
                                "Старые данные скрыты, не удалены",
                                Modifier.bodySMedium().color(AppColors.Muted),
                            )
                        }
                    }
                }
                VSpace(12)
            }

            Row(verticalAlignment = Alignment.CenterVertically) {
                // Restore full history button (visible only when baseline is set)
                if (baseline != null) {
                    SettingsChipButton(
                        label = if (baselineBusy) "…" else "Восстановить полную историю",
                        enabled = repo != null && !baselineBusy,
                        danger = false,
                    ) {
                        baselineBusy = true
                        baselineError = null
                        scope.launch {
                            try {
                                repo!!.clearAnalyticsBaseline()
                                baseline = null
                            } catch (e: Throwable) {
                                baselineError = e.message ?: "Не удалось сбросить точку отсчёта"
                            } finally {
                                baselineBusy = false
                            }
                        }
                    }
                    HSpace(10)
                }

                // Reset analytics button (always visible)
                SettingsChipButton(
                    label = "Сбросить аналитику",
                    enabled = repo != null && !baselineBusy,
                    danger = true,
                ) { showResetConfirm = true }
            }

            if (baselineError != null) {
                VSpace(8)
                SpanText(
                    baselineError!!,
                    Modifier.bodySMedium().color(AppColors.Error),
                )
            }
        }

        VSpace(16)

        // ── Боковая панель ─────────────────────────────────────────────────────
        SectionCard(
            title = "Боковая панель",
            subtitle = "Развёрнута по умолчанию · кликните по логотипу, чтобы свернуть",
        ) {
            SettingsToggleRow(
                label = if (sidebarOpen) "Развёрнута" else "Свёрнута",
                checked = sidebarOpen,
                onToggle = { container.setSidebarOpen(it) },
            )
        }

        VSpace(16)

        // ── Сессия ─────────────────────────────────────────────────────────────
        SectionCard(
            title = "Сессия",
            subtitle = "Вы вошли как ${session.user?.email ?: "—"}",
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Pill(
                    text = if (session.isAdmin) "ADMIN" else "USER",
                    tone = if (session.isAdmin) PillTone.Accent else PillTone.Neutral,
                )
                HSpace(8)
                Pill(
                    text = if (session.isAuthenticated) "Аутентифицирован" else "Анонимный",
                    tone = if (session.isAuthenticated) PillTone.Success else PillTone.Danger,
                )
            }
        }

        VSpace(16)

        // ── Диагностика гео ────────────────────────────────────────────────────
        GeoDebugSection(repo)

        VSpace(16)

        // ── О приложении ───────────────────────────────────────────────────────
        SectionCard(
            title = "О приложении",
            subtitle = "Arena Admin · сборка v0.1.0",
        ) {
            SpanText(
                "API · ${com.arenaproplay.admin.core.config.AppConfig.apiUrl}",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
        }

        VSpace(28)
    }

    // ── Overlay: подтверждение сброса аналитики ────────────────────────────────
    ConfirmActionOverlay(
        isOpen = showResetConfirm,
        onClose = { if (!baselineBusy) showResetConfirm = false },
        title = "Сбросить аналитику",
        description = "Аналитика начнёт считаться с этого момента. " +
            "Все дашборды, инсайты и поведенческая статистика будут " +
            "отсчитываться от сейчас. Пользователи, события, клубы и посты " +
            "НЕ удаляются — старые данные только скрываются от аналитики. " +
            "Кнопка «Восстановить полную историю» вернёт их обратно.",
        warning = null,
        error = baselineError,
        confirmLabel = if (baselineBusy) "Сбрасываем…" else "Сбросить от сейчас",
        confirmDanger = true,
        confirmEnabled = repo != null && !baselineBusy,
    ) {
        baselineBusy = true
        baselineError = null
        scope.launch {
            try {
                baseline = repo!!.resetAnalyticsBaseline()
                showResetConfirm = false
            } catch (e: Throwable) {
                baselineError = e.message ?: "Не удалось выполнить сброс аналитики"
                showResetConfirm = false
            } finally {
                baselineBusy = false
            }
        }
    }
}

// ── Private composables ───────────────────────────────────────────────────────

@Composable
private fun SettingsChipButton(
    label: String,
    enabled: Boolean,
    danger: Boolean,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .padding(leftRight = 16.px, topBottom = 9.px)
            .sBorderRadius(999)
            .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
            .styleModifier {
                if (danger) {
                    property("background", AppColors.Error.toRgbString())
                    property("color", AppColors.Surface.toRgbString())
                } else {
                    property("background", AppColors.Surface.toRgbString())
                    property("border", "1px solid ${AppColors.Ink.toRgbaString(0.12f)}")
                }
                if (!enabled) property("opacity", "0.5")
                property("transition", "all 180ms ease")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            Modifier.bodySMedium()
                .color(if (danger) AppColors.Surface else AppColors.Ink)
                .styleModifier { property("font-weight", "600") },
        )
    }
}

@Composable
private fun SettingsToggleRow(
    label: String,
    checked: Boolean,
    onToggle: (Boolean) -> Unit,
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        SpanText(label, Modifier.bodyMMedium().color(AppColors.Ink))
        HSpace(14)
        Box(
            Modifier
                .width(46.px).height(26.px)
                .sBorderRadius(999)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background", if (checked) AppColors.Ink.toRgbString() else AppColors.Ink.toRgbaString(0.12f))
                    property("position", "relative")
                    property("transition", "background 200ms ease")
                }
                .onClick { onToggle(!checked) },
        ) {
            Box(
                Modifier
                    .width(20.px).height(20.px)
                    .styleModifier {
                        property("background", AppColors.Surface.toRgbString())
                        property("border-radius", "50%")
                        property("position", "absolute")
                        property("top", "3px")
                        property("left", if (checked) "23px" else "3px")
                        property("transition", "left 200ms ease")
                        property("box-shadow", "0 1px 2px ${AppColors.Ink.toRgbaString(0.20f)}")
                    },
            )
        }
    }
}

// ── Диагностика гео: что сервер знает об IP админа ───────────────────────────

@Composable
private fun GeoDebugSection(repo: com.arenaproplay.admin.domain.admin.AdminRepository?) {
    var result by remember { mutableStateOf<com.arenaproplay.admin.domain.admin.GeoDebug?>(null) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    SectionCard(
        title = "Диагностика гео",
        subtitle = "Живая проверка: какой IP видит сервер и что отвечают гео-провайдеры",
    ) {
        Box(
            Modifier
                .sBorderRadius(10)
                .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                .classNames("na-press")
                .styleModifier {
                    property("background", "rgba(0,97,255,.22)")
                    property("border", "1px solid rgba(0,97,255,.45)")
                    property("padding", "8px 16px")
                    property("display", "inline-block")
                }
                .onClick {
                    if (!busy && repo != null) {
                        busy = true; error = null
                        scope.launch {
                            try { result = repo.getGeoDebug() } catch (e: Exception) {
                                error = e.message ?: "Ошибка"
                            } finally { busy = false }
                        }
                    }
                }
        ) {
            SpanText(
                if (busy) "Проверяю…" else "Проверить мой IP",
                Modifier.bodySMedium().color(AppColors.PrimaryLight),
            )
        }
        if (error != null) {
            VSpace(8)
            SpanText("Ошибка: $error", Modifier.bodySRegular().color(AppColors.Error))
        }
        val r = result
        if (r != null) {
            VSpace(12)
            SpanText(
                "IP клиента: ${r.callerIp ?: "—"}   ·   X-Forwarded-For: ${r.xffHeader ?: "—"}   ·   remote: ${r.remoteAddress}",
                Modifier.mono(12).color(AppColors.Ink).styleModifier { property("display", "block") },
            )
            VSpace(8)
            r.lookup.forEach { pr ->
                SpanText(
                    "${pr.provider}: HTTP ${pr.httpStatus} → " +
                        (if (pr.city != null || pr.countryCode != null) "${pr.city ?: "?"}, ${pr.countryCode ?: "?"}"
                         else "нет данных${pr.error?.let { " ($it)" } ?: ""}"),
                    Modifier.mono(12)
                        .color(if (pr.countryCode != null) AppColors.Success else AppColors.Warning)
                        .styleModifier { property("display", "block"); property("margin-top", "3px") },
                )
            }
            VSpace(8)
            SpanText(
                "Всего лукапов: ${r.snapshot.totalAttempts}, успешных: ${r.snapshot.totalSuccesses}" +
                    (r.snapshot.lastAttempt?.let { "  ·  последний: ${it.ip} @ ${it.timestamp}" } ?: ""),
                Modifier.bodySRegular().color(AppColors.Faint),
            )
        }
    }
}
